import React from 'react';
import { AthleteProfile } from '../../types';
import { verifyProAccess } from '../../utils/authUtils';
import { Crown, Lock, Sparkles, ShieldAlert } from 'lucide-react';

interface ProRestrictedWrapperProps {
  user: AthleteProfile | null | undefined;
  children: React.ReactNode;
  onOpenProCheckout?: (contextMessage?: string) => void;
  featureTitle?: string;
  featureDescription?: string;
  customContextMessage?: string;
  upsellMessage?: string;
  className?: string;
}

export const ProRestrictedWrapper: React.FC<ProRestrictedWrapperProps> = ({
  user,
  children,
  onOpenProCheckout,
  featureTitle = 'PRO Member Feature Restricted',
  featureDescription = 'Upgrade your account to Playground PRO to unlock deep historical analytics, career trend summaries, and scout reports.',
  customContextMessage,
  upsellMessage,
  className = '',
}) => {
  const isPro = verifyProAccess(user);
  const activeMessage = upsellMessage || customContextMessage || 'This historical performance deep-dive requires a PRO membership to unlock.';

  if (isPro) {
    return <>{children}</>;
  }

  return (
    <div className={`relative overflow-hidden rounded-[2.5rem] border-2 border-amber-400/40 bg-gradient-to-br from-indigo-950/90 via-slate-900 to-indigo-950 p-6 md:p-8 shadow-2xl ${className}`}>
      {/* Background Decorative Blur */}
      <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Blurred Preview Effect behind the locked overlay if children are present */}
      <div className="pointer-events-none select-none filter blur-sm opacity-25">
        {children}
      </div>

      {/* Locked Overlay Box */}
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 bg-slate-950/80 backdrop-blur-md text-center space-y-4">
        <div className="relative">
          <div className="p-4 bg-gradient-to-tr from-amber-400 via-amber-300 to-yellow-400 text-black rounded-2xl shadow-xl shadow-amber-400/30 ring-2 ring-amber-300">
            <Crown className="w-8 h-8 fill-black" />
          </div>
          <div className="absolute -top-1 -right-1 p-1 bg-rose-500 text-white rounded-full shadow border border-slate-900">
            <Lock className="w-3.5 h-3.5" />
          </div>
        </div>

        <div className="max-w-md space-y-1.5">
          <span className="px-3 py-0.5 bg-amber-400/20 text-amber-300 font-mono font-black text-[10px] uppercase rounded-full tracking-wider border border-amber-400/40 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> PRO SUBSCRIBER EXCLUSIVE
          </span>
          <h3 className="text-xl font-black italic uppercase text-white tracking-tight">
            {featureTitle}
          </h3>
          <p className="text-xs text-indigo-200/80 leading-relaxed font-medium">
            {featureDescription}
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            if (onOpenProCheckout) {
              onOpenProCheckout(activeMessage);
            }
          }}
          className="px-6 py-3 bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 hover:from-amber-300 hover:to-yellow-300 text-black font-black italic text-xs uppercase rounded-xl shadow-xl shadow-amber-400/30 transition transform hover:scale-105 flex items-center space-x-2"
        >
          <Crown className="w-4 h-4 fill-black" />
          <span>Upgrade to PRO Tier ($9.99/mo)</span>
        </button>
      </div>
    </div>
  );
};
